import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Logo } from '../components/Logo';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, Save, CheckCircle2, FileText } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const STEPS = [
  { key: 'medical', label: 'Historia Medyczna', icon: FileText },
  { key: 'pain', label: 'Ból', icon: FileText },
  { key: 'lifestyle', label: 'Styl Życia', icon: FileText },
  { key: 'goals', label: 'Cele', icon: FileText }
];

export const HealthInterviewPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  
  const [formData, setFormData] = useState({
    medical_history: {
      injuries: [],
      surgeries: [],
      chronic_conditions: [],
      medications: []
    },
    pain_history: {
      current_pain: [],
      past_pain: [],
      pain_triggers: []
    },
    lifestyle: {
      sleep_hours: null,
      stress_level: null,
      activity_level: '',
      occupation: '',
      sport_history: []
    },
    goals: {
      primary_goals: [],
      timeline: '',
      priorities: []
    },
    contraindications: [],
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      const [clientRes, interviewRes] = await Promise.all([
        axios.get(`${API_URL}/api/clients/${id}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/clients/${id}/health-interview`, { withCredentials: true })
      ]);
      
      setClient(clientRes.data);
      if (interviewRes.data && Object.keys(interviewRes.data).length > 0) {
        setFormData({ ...formData, ...interviewRes.data });
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API_URL}/api/clients/${id}/health-interview`,
        formData,
        { withCredentials: true }
      );
    } catch (error) {
      console.error('Error saving:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleComplete = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API_URL}/api/clients/${id}/health-interview`,
        { ...formData, completed: true },
        { withCredentials: true }
      );
      navigate(`/client/${id}`);
    } catch (error) {
      console.error('Error completing:', error);
      setSaving(false);
    }
  };

  const addArrayItem = (path, value) => {
    if (!value.trim()) return;
    const keys = path.split('.');
    const newData = { ...formData };
    let current = newData;
    for (let i = 0; i < keys.length - 1; i++) {
      current = current[keys[i]];
    }
    const lastKey = keys[keys.length - 1];
    current[lastKey] = [...(current[lastKey] || []), value];
    setFormData(newData);
  };

  const removeArrayItem = (path, index) => {
    const keys = path.split('.');
    const newData = { ...formData };
    let current = newData;
    for (let i = 0; i < keys.length - 1; i++) {
      current = current[keys[i]];
    }
    const lastKey = keys[keys.length - 1];
    current[lastKey] = current[lastKey].filter((_, i) => i !== index);
    setFormData(newData);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A]">
        <div className="text-white">Ładowanie...</div>
      </div>
    );
  }

  const renderMedicalHistory = () => (
    <div className="space-y-8">
      <div>
        <Label className="label-text text-[#A1A1A1] mb-3 block">URAZY</Label>
        <div className="space-y-2">
          {formData.medical_history.injuries.map((item, i) => (
            <div key={i} className="flex items-center gap-2">
              <div className="flex-1 body-text text-white p-3 bg-[#0A0A0A] rounded-2xl border border-[#2A2A2A]">
                {item}
              </div>
              <Button
                onClick={() => removeArrayItem('medical_history.injuries', i)}
                variant="ghost"
                size="sm"
                className="text-[#A1A1A1]"
              >
                ✕
              </Button>
            </div>
          ))}
          <Input
            placeholder="Dodaj uraz (Enter)"
            className="bg-[#0A0A0A] border-[#2A2A2A] rounded-2xl text-white"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                addArrayItem('medical_history.injuries', e.target.value);
                e.target.value = '';
              }
            }}
          />
        </div>
      </div>

      <div>
        <Label className="label-text text-[#A1A1A1] mb-3 block">OPERACJE</Label>
        <div className="space-y-2">
          {formData.medical_history.surgeries.map((item, i) => (
            <div key={i} className="flex items-center gap-2">
              <div className="flex-1 body-text text-white p-3 bg-[#0A0A0A] rounded-2xl border border-[#2A2A2A]">
                {item}
              </div>
              <Button
                onClick={() => removeArrayItem('medical_history.surgeries', i)}
                variant="ghost"
                size="sm"
                className="text-[#A1A1A1]"
              >
                ✕
              </Button>
            </div>
          ))}
          <Input
            placeholder="Dodaj operację (Enter)"
            className="bg-[#0A0A0A] border-[#2A2A2A] rounded-2xl text-white"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                addArrayItem('medical_history.surgeries', e.target.value);
                e.target.value = '';
              }
            }}
          />
        </div>
      </div>

      <div>
        <Label className="label-text text-[#A1A1A1] mb-3 block">CHOROBY PRZEWLEKŁE</Label>
        <div className="space-y-2">
          {formData.medical_history.chronic_conditions.map((item, i) => (
            <div key={i} className="flex items-center gap-2">
              <div className="flex-1 body-text text-white p-3 bg-[#0A0A0A] rounded-2xl border border-[#2A2A2A]">
                {item}
              </div>
              <Button
                onClick={() => removeArrayItem('medical_history.chronic_conditions', i)}
                variant="ghost"
                size="sm"
                className="text-[#A1A1A1]"
              >
                ✕
              </Button>
            </div>
          ))}
          <Input
            placeholder="Dodaj chorobę (Enter)"
            className="bg-[#0A0A0A] border-[#2A2A2A] rounded-2xl text-white"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                addArrayItem('medical_history.chronic_conditions', e.target.value);
                e.target.value = '';
              }
            }}
          />
        </div>
      </div>

      <div>
        <Label className="label-text text-[#A1A1A1] mb-3 block">LEKI</Label>
        <div className="space-y-2">
          {formData.medical_history.medications.map((item, i) => (
            <div key={i} className="flex items-center gap-2">
              <div className="flex-1 body-text text-white p-3 bg-[#0A0A0A] rounded-2xl border border-[#2A2A2A]">
                {item}
              </div>
              <Button
                onClick={() => removeArrayItem('medical_history.medications', i)}
                variant="ghost"
                size="sm"
                className="text-[#A1A1A1]"
              >
                ✕
              </Button>
            </div>
          ))}
          <Input
            placeholder="Dodaj lek (Enter)"
            className="bg-[#0A0A0A] border-[#2A2A2A] rounded-2xl text-white"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                addArrayItem('medical_history.medications', e.target.value);
                e.target.value = '';
              }
            }}
          />
        </div>
      </div>
    </div>
  );

  const renderPainHistory = () => (
    <div className="space-y-8">
      <div>
        <Label className="label-text text-[#A1A1A1] mb-3 block">OBECNY BÓL</Label>
        <div className="space-y-2">
          {formData.pain_history.current_pain.map((item, i) => (
            <div key={i} className="flex items-center gap-2">
              <div className="flex-1 body-text text-white p-3 bg-[#0A0A0A] rounded-2xl border border-[#2A2A2A]">
                {item}
              </div>
              <Button
                onClick={() => removeArrayItem('pain_history.current_pain', i)}
                variant="ghost"
                size="sm"
                className="text-[#A1A1A1]"
              >
                ✕
              </Button>
            </div>
          ))}
          <Input
            placeholder="Dodaj opis bólu (Enter)"
            className="bg-[#0A0A0A] border-[#2A2A2A] rounded-2xl text-white"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                addArrayItem('pain_history.current_pain', e.target.value);
                e.target.value = '';
              }
            }}
          />
        </div>
      </div>

      <div>
        <Label className="label-text text-[#A1A1A1] mb-3 block">PRZESZŁY BÓL</Label>
        <div className="space-y-2">
          {formData.pain_history.past_pain.map((item, i) => (
            <div key={i} className="flex items-center gap-2">
              <div className="flex-1 body-text text-white p-3 bg-[#0A0A0A] rounded-2xl border border-[#2A2A2A]">
                {item}
              </div>
              <Button
                onClick={() => removeArrayItem('pain_history.past_pain', i)}
                variant="ghost"
                size="sm"
                className="text-[#A1A1A1]"
              >
                ✕
              </Button>
            </div>
          ))}
          <Input
            placeholder="Dodaj przeszły ból (Enter)"
            className="bg-[#0A0A0A] border-[#2A2A2A] rounded-2xl text-white"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                addArrayItem('pain_history.past_pain', e.target.value);
                e.target.value = '';
              }
            }}
          />
        </div>
      </div>

      <div>
        <Label className="label-text text-[#A1A1A1] mb-3 block">WYZWALACZE BÓLU</Label>
        <div className="space-y-2">
          {formData.pain_history.pain_triggers.map((item, i) => (
            <div key={i} className="flex items-center gap-2">
              <div className="flex-1 body-text text-white p-3 bg-[#0A0A0A] rounded-2xl border border-[#2A2A2A]">
                {item}
              </div>
              <Button
                onClick={() => removeArrayItem('pain_history.pain_triggers', i)}
                variant="ghost"
                size="sm"
                className="text-[#A1A1A1]"
              >
                ✕
              </Button>
            </div>
          ))}
          <Input
            placeholder="Dodaj wyzwalacz (Enter)"
            className="bg-[#0A0A0A] border-[#2A2A2A] rounded-2xl text-white"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                addArrayItem('pain_history.pain_triggers', e.target.value);
                e.target.value = '';
              }
            }}
          />
        </div>
      </div>
    </div>
  );

  const renderLifestyle = () => (
    <div className="space-y-6">
      <div>
        <Label className="label-text text-[#A1A1A1] mb-3 block">SEN (GODZINY)</Label>
        <Input
          type="number"
          step="0.5"
          value={formData.lifestyle.sleep_hours || ''}
          onChange={(e) => setFormData({
            ...formData,
            lifestyle: { ...formData.lifestyle, sleep_hours: parseFloat(e.target.value) || null }
          })}
          className="bg-[#0A0A0A] border-[#2A2A2A] rounded-2xl text-white"
        />
      </div>

      <div>
        <Label className="label-text text-[#A1A1A1] mb-3 block">POZIOM STRESU (1-10)</Label>
        <Input
          type="number"
          min="1"
          max="10"
          value={formData.lifestyle.stress_level || ''}
          onChange={(e) => setFormData({
            ...formData,
            lifestyle: { ...formData.lifestyle, stress_level: parseInt(e.target.value) || null }
          })}
          className="bg-[#0A0A0A] border-[#2A2A2A] rounded-2xl text-white"
        />
      </div>

      <div>
        <Label className="label-text text-[#A1A1A1] mb-3 block">POZIOM AKTYWNOŚCI</Label>
        <Select
          value={formData.lifestyle.activity_level}
          onValueChange={(value) => setFormData({
            ...formData,
            lifestyle: { ...formData.lifestyle, activity_level: value }
          })}
        >
          <SelectTrigger className="bg-[#0A0A0A] border-[#2A2A2A] rounded-2xl text-white">
            <SelectValue placeholder="Wybierz..." />
          </SelectTrigger>
          <SelectContent className="bg-[#1E1E1E] border-[#2A2A2A] text-white">
            <SelectItem value="sedentary">Siedzący</SelectItem>
            <SelectItem value="light">Lekko aktywny</SelectItem>
            <SelectItem value="moderate">Umiarkowanie aktywny</SelectItem>
            <SelectItem value="active">Bardzo aktywny</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label className="label-text text-[#A1A1A1] mb-3 block">ZAWÓD</Label>
        <Input
          value={formData.lifestyle.occupation || ''}
          onChange={(e) => setFormData({
            ...formData,
            lifestyle: { ...formData.lifestyle, occupation: e.target.value }
          })}
          className="bg-[#0A0A0A] border-[#2A2A2A] rounded-2xl text-white"
        />
      </div>

      <div>
        <Label className="label-text text-[#A1A1A1] mb-3 block">HISTORIA SPORTOWA</Label>
        <div className="space-y-2">
          {formData.lifestyle.sport_history.map((item, i) => (
            <div key={i} className="flex items-center gap-2">
              <div className="flex-1 body-text text-white p-3 bg-[#0A0A0A] rounded-2xl border border-[#2A2A2A]">
                {item}
              </div>
              <Button
                onClick={() => removeArrayItem('lifestyle.sport_history', i)}
                variant="ghost"
                size="sm"
                className="text-[#A1A1A1]"
              >
                ✕
              </Button>
            </div>
          ))}
          <Input
            placeholder="Dodaj sport (Enter)"
            className="bg-[#0A0A0A] border-[#2A2A2A] rounded-2xl text-white"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                addArrayItem('lifestyle.sport_history', e.target.value);
                e.target.value = '';
              }
            }}
          />
        </div>
      </div>
    </div>
  );

  const renderGoals = () => (
    <div className="space-y-8">
      <div>
        <Label className="label-text text-[#A1A1A1] mb-3 block">GŁÓWNE CELE</Label>
        <div className="space-y-2">
          {formData.goals.primary_goals.map((item, i) => (
            <div key={i} className="flex items-center gap-2">
              <div className="flex-1 body-text text-white p-3 bg-[#0A0A0A] rounded-2xl border border-[#2A2A2A]">
                {item}
              </div>
              <Button
                onClick={() => removeArrayItem('goals.primary_goals', i)}
                variant="ghost"
                size="sm"
                className="text-[#A1A1A1]"
              >
                ✕
              </Button>
            </div>
          ))}
          <Input
            placeholder="Dodaj cel (Enter)"
            className="bg-[#0A0A0A] border-[#2A2A2A] rounded-2xl text-white"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                addArrayItem('goals.primary_goals', e.target.value);
                e.target.value = '';
              }
            }}
          />
        </div>
      </div>

      <div>
        <Label className="label-text text-[#A1A1A1] mb-3 block">HARMONOGRAM</Label>
        <Input
          value={formData.goals.timeline || ''}
          onChange={(e) => setFormData({
            ...formData,
            goals: { ...formData.goals, timeline: e.target.value }
          })}
          placeholder="np. 3 miesiące, 6 miesięcy"
          className="bg-[#0A0A0A] border-[#2A2A2A] rounded-2xl text-white"
        />
      </div>

      <div>
        <Label className="label-text text-[#A1A1A1] mb-3 block">PRIORYTETY</Label>
        <div className="space-y-2">
          {formData.goals.priorities.map((item, i) => (
            <div key={i} className="flex items-center gap-2">
              <div className="flex-1 body-text text-white p-3 bg-[#0A0A0A] rounded-2xl border border-[#2A2A2A]">
                {item}
              </div>
              <Button
                onClick={() => removeArrayItem('goals.priorities', i)}
                variant="ghost"
                size="sm"
                className="text-[#A1A1A1]"
              >
                ✕
              </Button>
            </div>
          ))}
          <Input
            placeholder="Dodaj priorytet (Enter)"
            className="bg-[#0A0A0A] border-[#2A2A2A] rounded-2xl text-white"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                addArrayItem('goals.priorities', e.target.value);
                e.target.value = '';
              }
            }}
          />
        </div>
      </div>

      <div>
        <Label className="label-text text-[#A1A1A1] mb-3 block">PRZECIWWSKAZANIA</Label>
        <div className="space-y-2">
          {formData.contraindications.map((item, i) => (
            <div key={i} className="flex items-center gap-2">
              <div className="flex-1 body-text text-white p-3 bg-[#0A0A0A] rounded-2xl border border-[#2A2A2A]">
                {item}
              </div>
              <Button
                onClick={() => removeArrayItem('contraindications', i)}
                variant="ghost"
                size="sm"
                className="text-[#A1A1A1]"
              >
                ✕
              </Button>
            </div>
          ))}
          <Input
            placeholder="Dodaj przeciwwskazanie (Enter)"
            className="bg-[#0A0A0A] border-[#2A2A2A] rounded-2xl text-white"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                addArrayItem('contraindications', e.target.value);
                e.target.value = '';
              }
            }}
          />
        </div>
      </div>

      <div>
        <Label className="label-text text-[#A1A1A1] mb-3 block">DODATKOWE NOTATKI</Label>
        <Textarea
          value={formData.notes || ''}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          className="bg-[#0A0A0A] border-[#2A2A2A] rounded-2xl text-white min-h-[150px]"
        />
      </div>
    </div>
  );

  const renderStep = () => {
    switch (currentStep) {
      case 0: return renderMedicalHistory();
      case 1: return renderPainHistory();
      case 2: return renderLifestyle();
      case 3: return renderGoals();
      default: return null;
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="health-interview-page">
      <nav className="border-b border-[#2A2A2A] px-4 sm:px-8 py-4 sm:py-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-2">
          <Logo size="md" />
          <Button
            onClick={() => navigate(`/client/${id}`)}
            variant="ghost"
            className="text-[#A1A1A1] hover:text-white px-2 sm:px-4 shrink-0"
          >
            <ArrowLeft className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Powrót</span>
          </Button>
        </div>
      </nav>

      <main className="max-w-5xl mx-auto px-4 sm:px-8 py-8 sm:py-12">
        <div className="mb-12">
          <h1 className="display-text text-4xl sm:text-5xl text-white mb-2 break-words">
            {client?.name}
          </h1>
          <p className="body-text text-[#A1A1A1] text-lg">Wywiad Zdrowotny</p>
        </div>

        {/* Steps */}
        <div className="flex items-center gap-2 mb-8 sm:mb-12 overflow-x-auto pb-4">
          {STEPS.map((step, index) => (
            <button
              key={step.key}
              onClick={() => setCurrentStep(index)}
              className={`flex items-center gap-3 px-4 sm:px-6 py-3 rounded-full border transition-all whitespace-nowrap shrink-0 ${
                currentStep === index
                  ? 'bg-[#1E1E1E] border-[#C9A84C] text-white'
                  : 'bg-[#0A0A0A] border-[#2A2A2A] text-[#A1A1A1] hover:border-[#C9A84C]/50'
              }`}
            >
              <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                currentStep === index ? 'bg-[#C9A84C] text-[#0A0A0A]' : 'bg-[#2A2A2A] text-[#A1A1A1]'
              }`}>
                {index + 1}
              </span>
              <span className="label-text text-xs">{step.label}</span>
            </button>
          ))}
        </div>

        {/* Form */}
        <div className="card-surface rounded-3xl p-5 sm:p-8 md:p-12 mb-8">
          {renderStep()}
        </div>

        {/* Actions */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Button
            onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
            disabled={currentStep === 0}
            variant="ghost"
            className="text-[#A1A1A1]"
          >
            ← Poprzedni
          </Button>

          <div className="flex gap-2 sm:gap-4 ml-auto">
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-[#1E1E1E] hover:bg-[#2A2A2A] text-white border border-[#2A2A2A] rounded-full px-5 sm:px-8"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Zapisywanie...' : 'Zapisz'}
            </Button>

            {currentStep === STEPS.length - 1 ? (
              <Button
                onClick={handleComplete}
                disabled={saving}
                className="bg-[#1E1E1E] hover:bg-[#2A2A2A] text-white border border-[#C9A84C] rounded-full px-5 sm:px-8"
              >
                <CheckCircle2 className="w-4 h-4 mr-2" />
                Ukończ wywiad
              </Button>
            ) : (
              <Button
                onClick={() => setCurrentStep(Math.min(STEPS.length - 1, currentStep + 1))}
                className="bg-[#1E1E1E] hover:bg-[#2A2A2A] text-white border border-[#C9A84C] rounded-full px-5 sm:px-8"
              >
                Następny →
              </Button>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};